import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CheckCircle2, ClipboardList, Receipt } from "lucide-react";

import { dismissDashboardAlert } from "@/services/dashboardService";
import { dismissAllInvoiceRejections } from "@/services/invoiceService";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { cn } from "@/lib/utils";
import {
  flattenAgenda,
  TAX_I18N,
} from "@/pages/dashboard/dashboardAlertsModel";
import { formatDisplayDate } from "@/lib/dateFormat";
import { getFiscalAgenda } from "@/services/fiscalService";
import type { FiscalAgendaObligation } from "@/services/fiscalService";
import type {
  DashboardAlert,
  DashboardFinancial,
  DashboardMetrics,
} from "@fiscalo/shared-types";

interface DashboardFiscalOperationStatusProps {
  metrics: DashboardMetrics;
  financial: DashboardFinancial;
  alerts: DashboardAlert[];
  dismissedAlertIds: string[];
  recurringDraftsReadyCount: number;
  showPaidFiscalDashboard: boolean;
}

export function DashboardFiscalOperationStatus({
  metrics,
  financial,
  alerts,
  dismissedAlertIds,
  recurringDraftsReadyCount,
  showPaidFiscalDashboard,
}: DashboardFiscalOperationStatusProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();

  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", year + "-01", year + "-12"],
    queryFn: () => getFiscalAgenda(year + "-01", year + "-12"),
    enabled: showPaidFiscalDashboard,
  });

  const allObligations = useMemo(
    () => (agendaGrouped ? flattenAgenda(agendaGrouped) : []),
    [agendaGrouped]
  );

  const { nextObligation, dueThisMonthCount } = useMemo(() => {
    // "Próximo vencimiento" must mean FUTURE — without the date guard,
    // overdue rows (still status='pending' but with a past dueDate)
    // were leaking through as the next deadline, e.g. BPS 15 ene 2026
    // showing on the May dashboard. The separate "Próximos
    // vencimientos" panel already filters this way; mirror it here.
    const todayIso = new Date().toISOString().slice(0, 10);
    const list = allObligations.filter(
      (o) => o.status !== "completed" && o.dueDate >= todayIso,
    );
    const first = list[0] ?? null;
    if (!first) return { nextObligation: null as FiscalAgendaObligation | null, dueThisMonthCount: 0 };
    const targetMonth = first.dueDate.slice(0, 7);
    const dueThisMonth = list.filter((o) => o.dueDate.slice(0, 7) === targetMonth).length;
    return { nextObligation: first, dueThisMonthCount: dueThisMonth };
  }, [allObligations]);

  const reviewDismissId = useMemo(
    () =>
      `fiscal-op-review:r${metrics.rejected}:p${metrics.pending}:d${recurringDraftsReadyCount}`,
    [metrics.rejected, metrics.pending, recurringDraftsReadyCount]
  );
  const isDismissed = dismissedAlertIds.includes(reviewDismissId);

  const queryClient = useQueryClient();
  const dismissMutation = useMutation({
    mutationFn: () => dismissDashboardAlert(reviewDismissId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
  /**
   * The rejected-invoices banner needs to flip the per-invoice `rejectionDismissedAt`
   * (the field metrics.rejected actually filters on), not just the cosmetic dashboard-
   * alert dismissal table. Without this, clicking Descartar hid the banner only until
   * the next rejection arrived — the dismissed alert ID was keyed on `r${count}`, so
   * any count change resurfaced the banner. The bulk endpoint sets rejectionDismissedAt
   * on every currently-rejected, unresolved invoice; new rejections later still alert.
   */
  const dismissAllRejectionsMutation = useMutation({
    mutationFn: () => dismissAllInvoiceRejections(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  const ivaAmount = financial.ivaAPagarEstimadoUyu;
  const hasIva = showPaidFiscalDashboard && typeof ivaAmount === "number";
  const ivaIsCredit = hasIva && ivaAmount < 0;

  // Each item carries an explicit tone so the rendered icon tile picks the
  // right intent. Sniffing the icon className for Tailwind color names didn't
  // work — none of these classes use Tailwind color tokens (they use
  // brand vars like --fx-warn / hex codes), so every row was collapsing into
  // the "success" tone and rendering with a green tile.
  type ItemTone = "alert" | "warning" | "neutral" | "success";
  const summaryItems: Array<{
    key: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: ItemTone;
    text: string;
    dismissable?: boolean;
  }> = [];

  if (hasIva) {
    summaryItems.push({
      key: "iva",
      icon: Receipt,
      tone: ivaIsCredit ? "success" : "warning",
      text: ivaIsCredit
        ? t("dashboard.fiscalOperation.summaryIvaCredit", {
            amount: formatDashboardCurrency(Math.abs(ivaAmount)),
          })
        : t("dashboard.fiscalOperation.summaryIva", {
            amount: formatDashboardCurrency(ivaAmount),
          }),
    });
  }

  if (showPaidFiscalDashboard && nextObligation) {
    summaryItems.push({
      key: "deadlines",
      icon: CalendarClock,
      tone: "neutral",
      text: dueThisMonthCount > 1
        ? t("dashboard.fiscalOperation.summaryNextDeadlineWithMore", {
            tax: t(TAX_I18N[nextObligation.taxType] ?? nextObligation.taxType),
            date: formatDisplayDate(nextObligation.dueDate, {
              useCalendarDate: true,
              locale: i18n.language,
            }),
            moreCount: dueThisMonthCount - 1,
          })
        : t("dashboard.fiscalOperation.summaryNextDeadline", {
            tax: t(TAX_I18N[nextObligation.taxType] ?? nextObligation.taxType),
            date: formatDisplayDate(nextObligation.dueDate, {
              useCalendarDate: true,
              locale: i18n.language,
            }),
          }),
    });
  }

  /**
   * `metrics.rejected` already filters on `rejectionDismissedAt IS NULL` (backend),
   * so once the bulk-dismiss endpoint flips that field for the current rejections,
   * the count drops to 0 and the item naturally disappears — no need to also
   * gate on the dashboard-alert `isDismissed` flag, which was the stale path that
   * caused this banner to re-appear with each new rejection.
   */
  if (metrics.rejected > 0) {
    summaryItems.push({
      key: "rejected",
      icon: AlertTriangle,
      tone: "alert",
      text: t("dashboard.fiscalOperation.summaryRejected", {
        count: metrics.rejected,
      }),
      dismissable: true,
    });
  }

  if ((metrics.pending > 0 || recurringDraftsReadyCount > 0) && !isDismissed) {
    summaryItems.push({
      key: "pending",
      icon: AlertTriangle,
      tone: "warning",
      text: [
        metrics.pending > 0
          ? t("dashboard.fiscalOperation.summaryPendingInvoices", {
              count: metrics.pending,
            })
          : null,
        recurringDraftsReadyCount > 0
          ? t("dashboard.fiscalOperation.summaryRecurringDrafts", {
              count: recurringDraftsReadyCount,
            })
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      dismissable: true,
    });
  }

  // Banner mirrors what the user can actually see in summaryItems.
  // The previous version triggered on `effectiveBreakdown.band !== "healthy"`
  // or `visibleAlertsCount > 0` — neither of which produces a row here —
  // so a tenant with e.g. a `cae_expiring` API alert (counted by the
  // alerts bar elsewhere) or a sub-80 score saw "Hay cosas para revisar"
  // with an empty summary list. The score lives in SaludFiscalHero;
  // external alerts live in the alerts bar — this card is for
  // operational items (rejections, pending DGI, recurring drafts).
  const showReview = summaryItems.length > 0 && !isDismissed;
  const dismissableCount = summaryItems.filter((it) => it.dismissable).length;

  return (
    <div className="fx-panel">
      <div className="fx-panel-head">
        <div className="title">
          <span className="ico-tile olive">
            <ClipboardList className="h-4 w-4" />
          </span>
          <span>{t("dashboard.fiscalOperation.title")}</span>
        </div>
        {dismissableCount > 0 && (
          <span className="right-meta">
            {t("dashboard.fiscalOperation.actionsToReview", {
              count: dismissableCount,
              defaultValue: "{{count}} acciones para revisar",
            })}
          </span>
        )}
      </div>
      <div className={cn("fx-banner", showReview ? "warning" : "success")}>
        {showReview ? (
          <AlertTriangle className="h-5 w-5 shrink-0" />
        ) : (
          <CheckCircle2 className="h-5 w-5 shrink-0" />
        )}
        <span>
          {showReview
            ? t("dashboard.fiscalOperation.needsReview")
            : t("dashboard.fiscalOperation.allOk")}
        </span>
      </div>
      {summaryItems.length > 0 ? (
        <ul className="fx-issues">
          {summaryItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key} className="fx-issue">
                <span className={`ico ${item.tone}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="txt">{item.text}</span>
                {item.dismissable ? (
                  item.key === "rejected" ? (
                    <button
                      type="button"
                      className="dismiss"
                      onClick={() => dismissAllRejectionsMutation.mutate()}
                      disabled={dismissAllRejectionsMutation.isPending}
                    >
                      {t("dashboard.fiscalOperation.dismiss", "Descartar")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="dismiss"
                      onClick={() => dismissMutation.mutate()}
                      disabled={dismissMutation.isPending}
                    >
                      {t("dashboard.fiscalOperation.dismiss", "Descartar")}
                    </button>
                  )
                ) : (
                  <span />
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-6 py-5 text-sm" style={{ color: "var(--fx-ink-500)" }}>
          {t("dashboard.fiscalOperation.summaryFallback")}
        </div>
      )}
    </div>
  );
}
